import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPIAdmin } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';

export const addVehicleType = createAsyncThunk(
    'vehicleType/add',
    async (vehicleTypeData, { rejectWithValue }) => {
        try {
            const response = await callAPIAdmin(apiUrls.adminAddVehicleType, {}, 'POST', vehicleTypeData);
            if (response?.data?.status) {
                return response.data.data;
            }
            return rejectWithValue(response?.data?.message || 'Failed to add vehicle type');
        } catch (error) {
            return rejectWithValue(error?.response?.data?.message || error?.message);
        }
    }
);

export const updateVehicleType = createAsyncThunk(
    'vehicleType/update',
    async ({ id, vehicleTypeData }, { rejectWithValue }) => {
        try {
            const response = await callAPIAdmin(apiUrls.adminUpdateVehicle, {}, 'PATCH', { _id: id, ...vehicleTypeData });
            if (response?.data?.status) {
                return response.data.data;
            }
            return rejectWithValue(response?.data?.message || 'Failed to update vehicle type');
        } catch (error) {
            return rejectWithValue(error?.response?.data?.message || error?.message);
        }
    }
);

export const deleteVehicleType = createAsyncThunk(
    'vehicleType/delete',
    async (id, { rejectWithValue }) => {
        try {
            const response = await callAPIAdmin(apiUrls.adminDeleteVehicleType, { _id: id }, 'DELETE', {});
            if (response?.data?.status) {
                return { id, message: response.data.message };
            }
            return rejectWithValue(response?.data?.message || 'Failed to delete vehicle type');
        } catch (error) {
            return rejectWithValue(error?.response?.data?.message || error?.message);
        }
    }
);

export const updateVehicleTypeStatus = createAsyncThunk(
    'vehicleType/updateStatus',
    async ({ id, status }, { rejectWithValue }) => {
        try {
            const response = await callAPIAdmin(apiUrls.adminUpdateStatusVehicleType, { _id: id, status }, 'PATCH', {});
            if (response?.data?.status) {
                return { id, status, message: response.data.message };
            }
            return rejectWithValue(response?.data?.message || 'Failed to update status');
        } catch (error) {
            return rejectWithValue(error?.response?.data?.message || error?.message);
        }
    }
);

const initialState = {
    mutationLoading: false,
    mutationError: null,
    mutationSuccess: null,
};

const adminVehicleTypeSlice = createSlice({
    name: 'vehicleType',
    initialState,
    reducers: {
        clearMutationError: (state) => {
            state.mutationError = null;
        },
        clearMutationSuccess: (state) => {
            state.mutationSuccess = null;
        },
        resetMutationState: (state) => {
            state.mutationLoading = false;
            state.mutationError = null;
            state.mutationSuccess = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(addVehicleType.pending, (state) => {
                state.mutationLoading = true;
                state.mutationError = null;
                state.mutationSuccess = null;
            })
            .addCase(addVehicleType.fulfilled, (state) => {
                state.mutationLoading = false;
                state.mutationSuccess = 'Vehicle type added successfully';
            })
            .addCase(addVehicleType.rejected, (state, action) => {
                state.mutationLoading = false;
                state.mutationError = action.payload;
            })
            .addCase(updateVehicleType.pending, (state) => {
                state.mutationLoading = true;
                state.mutationError = null;
                state.mutationSuccess = null;
            })
            .addCase(updateVehicleType.fulfilled, (state) => {
                state.mutationLoading = false;
                state.mutationSuccess = 'Vehicle type updated successfully';
            })
            .addCase(updateVehicleType.rejected, (state, action) => {
                state.mutationLoading = false;
                state.mutationError = action.payload;
            })
            .addCase(deleteVehicleType.pending, (state) => {
                state.mutationLoading = true;
                state.mutationError = null;
                state.mutationSuccess = null;
            })
            .addCase(deleteVehicleType.fulfilled, (state) => {
                state.mutationLoading = false;
                state.mutationSuccess = 'Vehicle type deleted successfully';
            })
            .addCase(deleteVehicleType.rejected, (state, action) => {
                state.mutationLoading = false;
                state.mutationError = action.payload;
            })
            .addCase(updateVehicleTypeStatus.pending, (state) => {
                state.mutationLoading = true;
                state.mutationError = null;
                state.mutationSuccess = null;
            })
            .addCase(updateVehicleTypeStatus.fulfilled, (state, action) => {
                state.mutationLoading = false;
                state.mutationSuccess = `Status changed to ${action.payload?.status}`;
            })
            .addCase(updateVehicleTypeStatus.rejected, (state, action) => {
                state.mutationLoading = false;
                state.mutationError = action.payload;
            });
    },
});

export const { clearMutationError, clearMutationSuccess, resetMutationState } = adminVehicleTypeSlice.actions;
export default adminVehicleTypeSlice.reducer;